// @ts-nocheck
/**
 * Factory for creating order page Redux slices.
 *
 * All three order variants (Store, SKU, Kit) share identical reducer logic
 * with minor config differences. This factory eliminates ~100 LOC of duplication.
 *
 * Usage:
 *   const { slice, initialState } = createOrderSlice({
 *     name: 'orderByStore',
 *     columnKeys: ['sku', 'store', 'warehouse', 'order'],
 *     hasAvailability: false,
 *   });
 */
import { createSlice } from '@reduxjs/toolkit';

interface CreateOrderSliceConfig {
	/** Redux slice name (e.g. 'orderByStore', 'orderBySku', 'orderByKit') */
	name: string;
	/** Keys for the columns object. First key is the entity-specific one (e.g. 'sku' or 'kit') */
	columnKeys: string[];
	/** Whether this variant tracks availability state */
	hasAvailability?: boolean;
}

export function createOrderSlice(config: CreateOrderSliceConfig) {
	const { name, columnKeys, hasAvailability = false } = config;

	// Build columns initial state: { sku: [], store: [], warehouse: [], order: [] }
	const columnsInit: Record<string, any[]> = {};
	for (const key of columnKeys) {
		columnsInit[key] = [];
	}

	const initialState: any = {
		columns: columnsInit,
		orderData: { rows: [], totalCount: 0 },
		loading: {
			columns: false,
			data: true,
			backgroundData: false,
			...(hasAvailability ? { availability: false } : {}),
		},
	};

	if (hasAvailability) {
		initialState.availability = {};
	}

	// Build reducers
	const reducers: Record<string, any> = {
		setColumns(state, { payload }) {
			const columns: Record<string, any[]> = {};
			for (const key of columnKeys) {
				columns[key] = payload[key];
			}
			return { ...state, columns };
		},
		setOrderData(state, { payload: { rows, totalCount } }) {
			return { ...state, orderData: { rows, totalCount } };
		},
		appendOrderData(state, { payload: { rows } }) {
			const merged = [...state.orderData.rows, ...rows];
			return { ...state, orderData: { ...state.orderData, rows: merged } };
		},
		setLoading(state, { payload }) {
			return { ...state, loading: { ...state.loading, ...payload } };
		},
		clearData() {
			return { ...initialState };
		},
	};

	if (hasAvailability) {
		reducers.setAvailability = (state, { payload }) => {
			return { ...state, availability: payload };
		};
	}

	const slice = createSlice({
		name,
		initialState,
		reducers,
	});

	return { slice, initialState };
}
